import React from 'react';
import { motion } from 'framer-motion';
import { Users, Target, Filter, Clock, BarChartIcon as ChartBar, Zap } from 'lucide-react';
import { Button } from '../ui/button';
import Footer from '../components/Footer';
import PremiumCTA from '../components/PremiumCTA';

const processSteps = [
  {
    icon: <Users className="w-12 h-12 text-[#E38E49]" />,
    title: "Client Consultation",
    description: "We start by understanding your unique needs and goals."
  },
  {
    icon: <Target className="w-12 h-12 text-[#E38E49]" />,
    title: "Audience Research",
    description: "Our team identifies high-net-worth individuals interested in immigration."
  },
  {
    icon: <Filter className="w-12 h-12 text-[#E38E49]" />,
    title: "Lead Qualification",
    description: "We meticulously screen each lead to ensure they meet your criteria."
  },
  {
    icon: <Clock className="w-12 h-12 text-[#E38E49]" />,
    title: "Real-Time Delivery",
    description: "Qualified leads are delivered to you instantly for quick follow-up."
  },
  {
    icon: <ChartBar className="w-12 h-12 text-[#E38E49]" />,
    title: "Performance Analysis",
    description: "We continuously monitor and analyze campaign performance."
  },
  {
    icon: <Zap className="w-12 h-12 text-[#E38E49]" />,
    title: "Ongoing Optimization",
    description: "Our team refines strategies to keep you ahead of the curve."
  }
];

const HowWeWork = () => {
  return (
    <div className="min-h-screen bg-[#D4EBF8] text-[#0A3981]">
      {/* Hero Section */}
      <section className="w-full px-4 py-12 sm:py-16 md:py-20 lg:py-24 xl:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#D4EBF8] to-[#F8FBFE] opacity-70"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-[#0A3981] to-[#1F509A]">
              How We Work
            </h1>
            <p className="text-xl md:text-2xl text-[#1F509A]/80 mb-8 max-w-3xl mx-auto">
              Discover our proven process for delivering high-quality HNWI leads for immigration programs
            </p>
            <Button size="lg" className="bg-[#E38E49] hover:bg-[#1F509A] text-white">
              Inquire Now
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Process Steps Section */}
      <section className="w-full px-4 py-16 bg-gray-100">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-[#0A3981]">Our Journey to Your Success</h2>
          <div className="relative">
            {/* Vertical Dotted Line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-dotted border-dotted border-navy-800 transform -translate-x-1/2"></div>

            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true, amount: 0.3 }}
                className="relative flex flex-col items-center mb-16 last:mb-0"
              >
                {/* Step Icon */}
                <div className="flex items-center justify-center w-16 h-16 bg-white rounded-full border-4 border-[#E38E49] mb-4 relative z-10">
                  {step.icon}
                </div>

                {/* Step Details */}
                <div className="text-center">
                  <h3 className="text-2xl font-semibold mb-2 text-[#0A3981]">{step.title}</h3>
                  <p className="text-[#1F509A]/80">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <PremiumCTA />
      <Footer />
    </div>
  );
};

export default HowWeWork;
